/* Compact feed-update button used in the feed header.
   Shows a circular progress ring around the refresh icon during updates.
   Hover popover shows progress details and a cancel option. */

import { useState } from "react";
import { RefreshCw, Check, X } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import type { FeedUpdateState } from "@/hooks/useFeedUpdateProgress";

export interface CompactFeedUpdateButtonProps {
  state: FeedUpdateState;
  progress: number;
  currentIndex: number;
  totalSources: number;
  timeUntilMidnight: string;
  lastUpdatedAt: number | undefined;
  onUpdate: () => void;
  onCancel: () => void;
}

function formatLastUpdated(epochMs: number): string {
  const diffMs = Date.now() - epochMs;
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}

const SIZE = 32;
const STROKE_WIDTH = 2.5;
const RADIUS = (SIZE - STROKE_WIDTH) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

export function CompactFeedUpdateButton({
  state,
  progress,
  currentIndex,
  totalSources,
  timeUntilMidnight,
  lastUpdatedAt,
  onUpdate,
  onCancel,
}: CompactFeedUpdateButtonProps) {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const isIdle = state === "idle";
  const isDone = state === "done";
  const offset = CIRCUMFERENCE * (1 - progress);

  const button = (
    <button
      type="button"
      onClick={isIdle ? onUpdate : undefined}
      onMouseEnter={() => !isIdle && setPopoverOpen(true)}
      onMouseLeave={() => setPopoverOpen(false)}
      className="relative flex items-center justify-center size-9 rounded-md hover:bg-accent transition-colors cursor-pointer"
      tabIndex={-1}
    >
      <div className="relative" style={{ width: SIZE, height: SIZE }}>
        {!isIdle && (
          <svg
            width={SIZE}
            height={SIZE}
            className="absolute inset-0 rotate-[-90deg]"
          >
            <circle
              cx={SIZE / 2}
              cy={SIZE / 2}
              r={RADIUS}
              fill="none"
              stroke="currentColor"
              strokeWidth={STROKE_WIDTH}
              className="text-muted-foreground/20"
            />
            <circle
              cx={SIZE / 2}
              cy={SIZE / 2}
              r={RADIUS}
              fill="none"
              stroke="currentColor"
              strokeWidth={STROKE_WIDTH}
              strokeLinecap="round"
              strokeDasharray={CIRCUMFERENCE}
              strokeDashoffset={offset}
              className={`transition-[stroke-dashoffset] duration-500 ease-out ${isDone ? "text-chart-2" : "text-primary"}`}
            />
          </svg>
        )}
        <span className="absolute inset-0 flex items-center justify-center">
          {isDone ? (
            <Check className="size-4 text-chart-2" />
          ) : (
            <RefreshCw
              className={`size-4 text-muted-foreground ${state === "updating" ? "animate-spin" : ""}`}
            />
          )}
        </span>
      </div>
    </button>
  );

  // When idle, show a simple tooltip (same as old behavior)
  if (isIdle) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>{button}</TooltipTrigger>
          <TooltipContent side="bottom">
            {lastUpdatedAt && (
              <p>Last updated {formatLastUpdated(lastUpdatedAt)}.</p>
            )}
            <p>Next auto-update in {timeUntilMidnight}.</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // When updating or done, show progress popover on hover
  return (
    <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
      <PopoverTrigger asChild>{button}</PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="end"
        className="p-3 w-56"
        onMouseEnter={() => setPopoverOpen(true)}
        onMouseLeave={() => setPopoverOpen(false)}
      >
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-foreground">
              {isDone ? "Update complete" : "Updating feeds…"}
            </span>
            <span className="text-xs text-muted-foreground">
              {currentIndex}/{totalSources}
            </span>
          </div>

          <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ease-out ${isDone ? "bg-chart-2" : "bg-primary"}`}
              style={{ width: `${Math.round(progress * 100)}%` }}
            />
          </div>

          {!isDone && (
            <Button
              variant="ghost"
              size="sm"
              className="w-full h-7 text-xs text-muted-foreground"
              onClick={onCancel}
            >
              <X className="size-3 mr-1" />
              Cancel update
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
